// React
import React from 'react';
import {useState, useEffect} from 'react';

// MUI
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import LoadingButton from '@mui/lab/LoadingButton';

// others
import { useFetch } from './hooks/useFetch.jsx';

const ENTITY_CONFIG = {
    controller: {
        endpoint: '/controllers/rename',
        idField: 'controllerId',
        maxLen: 32,
        label: 'controller',
    },
    deviceAssembly: {
        endpoint: '/device-assemblies/rename',
        idField: 'deviceAssemblyId',
        maxLen: 64,
        label: 'device',
    },
    device: {
        endpoint: '/devices/rename',
        idField: 'deviceId',
        maxLen: 64,
        label: 'sub-device',
    },
};

// reveal the .rename-btn pencil on hover only
export const hoverRenameSx = {
    '& .rename-btn': {visibility: 'hidden'},
    '&:hover .rename-btn': {visibility: 'visible'},
};

// small inline pencil icon (no icons package dependency)
export function RenameIcon(props) {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
            <path d="M17 3a2.83 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/>
        </svg>
    );
}

// icon button that opens the rename dialog without also triggering a parent row click.
// Carries the .rename-btn class so parents can reveal it on row hover only:
//   sx={{'& .rename-btn': {visibility: 'hidden'}, '&:hover .rename-btn': {visibility: 'visible'}}}
export function RenameIconButton(props) {
    const {onClick, title, className, ...other} = props;
    return (
        <IconButton
            size="small"
            className={"rename-btn" + (className ? " " + className : "")}
            aria-label={"rename " + (title || "")}
            title={"Rename " + (title || "")}
            onClick={(e) => {
                e.stopPropagation();
                onClick && onClick(e);
            }}
            {...other}
        >
            <RenameIcon />
        </IconButton>
    );
}

// Dialog for renaming a controller, device assembly or device.
// entityKind: 'controller' | 'deviceAssembly' | 'device'
// onRenamed(newName) is called after a successful rename.
export default function RenameDialog(props) {
    const {open, entityKind, entityId, initialName, onClose, onRenamed} = props;
    const [name, setName] = useState(initialName || "");
    const [saving, setSaving] = useState(false);
    const [errorText, setErrorText] = useState("");
    const fetchWrapper = useFetch();
    const config = ENTITY_CONFIG[entityKind];

    useEffect(() => {
        if (open) {
            setName(initialName || "");
            setErrorText("");
            setSaving(false);
        }
    }, [open, initialName]);

    if (!config) {
        return null;
    }

    function handleSave() {
        const trimmed = name.trim();
        if (!trimmed) {
            setErrorText("Name cannot be empty.");
            return;
        }
        if (trimmed.length > config.maxLen) {
            setErrorText("Name must be at most " + config.maxLen + " characters.");
            return;
        }
        setSaving(true);
        setErrorText("");
        const renameBody = {name: trimmed};
        renameBody[config.idField] = entityId;
        fetchWrapper.post(`https://api.${process.env.REACT_APP_DOMAIN}${config.endpoint}`, renameBody)
            .then(response => {
                if (response.ok)
                    return response.json();
                else if (response.status === 403)
                    throw new Error("You do not have permission to rename this " + config.label + ".");
                else if (response.status === 400)
                    throw new Error("The name is invalid.");
                else
                    return response.json();
            })
            .then(responseObj => {
                if (responseObj.error !== 0) {
                    throw new Error("Renaming failed. (" + responseObj.error + ")");
                }
                if (onRenamed) {
                    onRenamed(trimmed);
                }
                if (onClose) {
                    onClose();
                }
            })
            .catch(error => {
                setErrorText(error.message || "Renaming failed.");
            })
            .finally(() => {
                setSaving(false);
            });
    }

    function handleKeyDown(e) {
        if (e.key === "Enter" && !saving && name.trim()) {
            handleSave();
        }
    }

    return (
        <Dialog open={!!open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
            <DialogTitle>Rename {config.label}</DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    fullWidth
                    margin="dense"
                    label="Name"
                    value={name}
                    inputProps={{maxLength: config.maxLen}}
                    error={!!errorText}
                    helperText={errorText || (name.trim().length + "/" + config.maxLen)}
                    onChange={(e) => { setName(e.target.value); setErrorText(""); }}
                    onKeyDown={handleKeyDown}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>Cancel</Button>
                <LoadingButton loading={saving} variant="contained" onClick={handleSave} disabled={!name.trim()}>Save</LoadingButton>
            </DialogActions>
        </Dialog>
    );
}
